/**
 * API client wrapper for the Outlook Add-in.
 *
 * All API calls go through `apiFetch()` which:
 * - Always sets `credentials: 'include'` for cookie transmission
 * - Attaches CSRF double-submit token on unsafe methods
 * - On 401 response: attempts silent token refresh via `initSession()`, retries once
 * - Enforces a default request timeout (30 s) via AbortController
 * - If retry also fails, surfaces the error to the caller
 *
 * This replaces the old Bearer-token approach in api.ts.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { initSession } from "./authService";
import { getCsrfToken } from "./csrfStore";
const API_BASE = process.env.API_URL || "https://localhost:3001";
if (!API_BASE) {
    throw new Error("[apiClient] API_URL is not configured. Ensure webpack/vite defines process.env.API_URL.");
}
/** Default request timeout in milliseconds. */
const DEFAULT_TIMEOUT_MS = 90000;
// ─── Helpers ────────────────────────────────────────────────────────────────
/**
 * Reads a non-HttpOnly cookie value by name. Returns null if absent or if
 * document.cookie is inaccessible (e.g. partitioned iframe context).
 */
function readCookie(name) {
    try {
        const prefix = name + "=";
        const parts = document.cookie ? document.cookie.split("; ") : [];
        for (const part of parts) {
            if (part.startsWith(prefix)) {
                return decodeURIComponent(part.slice(prefix.length));
            }
        }
    }
    catch (_a) {
        // document.cookie may throw in sandboxed contexts
    }
    return null;
}
/**
 * Resolves the current CSRF token from cookie (primary) or in-memory store
 * (fallback for Outlook WebView2 partitioned-cookie contexts).
 */
function resolveCsrfToken() {
    return readCookie("csrf_token") || getCsrfToken();
}
/**
 * Parses an error response and throws with a descriptive message.
 * Attempts to extract `detail` from JSON body; falls back to status text.
 */
async function throwApiError(response, method, path) {
    let detail = `API request failed: ${response.status} ${response.statusText} on ${method} ${path}`;
    try {
        const err = await response.json();
        if (err === null || err === void 0 ? void 0 : err.detail) {
            detail = typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
        }
    }
    catch (_a) {
        /* response body is not JSON — keep the generic detail */
    }
    throw new Error(detail);
}
/**
 * Builds merged headers for a request including CSRF token when appropriate.
 */
function buildHeaders(isOwnApi, callerHeaders) {
    const base = { "Content-Type": "application/json" };
    if (isOwnApi) {
        const csrf = resolveCsrfToken();
        if (csrf)
            base["X-CSRF-Token"] = csrf;
    }
    const caller = callerHeaders instanceof Headers
        ? Object.fromEntries(callerHeaders.entries())
        : callerHeaders || {};
    return Object.assign(Object.assign({}, base), caller);
}
/**
 * Fetch wrapper that always includes credentials (session cookie) and
 * handles 401 responses with a single automatic retry after re-establishing
 * the session.
 *
 * @param url - Relative path (e.g. "/api/quote/extract") or full URL
 * @param options - Standard RequestInit options plus optional `timeoutMs`
 * @returns The fetch Response object
 * @throws Error if both the initial request and retry fail, or on timeout
 */
export async function apiFetch(url, options = {}) {
    const { timeoutMs = DEFAULT_TIMEOUT_MS } = options, fetchOptions = __rest(options, ["timeoutMs"]);
    const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
    // Only attach CSRF to requests aimed at our own API
    const isOwnApi = !url.startsWith("http") || fullUrl.startsWith(API_BASE);
    const requestInit = Object.assign(Object.assign({ credentials: "include" }, fetchOptions), { headers: buildHeaders(isOwnApi, fetchOptions.headers) });
    let response = await timedFetch(fullUrl, requestInit, timeoutMs);
    if (response.status === 401) {
        // Attempt silent session renewal
        const renewed = await initSession();
        if (renewed) {
            // Rebuild headers — session renewal may have rotated the CSRF token
            const retryInit = Object.assign(Object.assign({}, requestInit), { headers: buildHeaders(isOwnApi, fetchOptions.headers) });
            response = await timedFetch(fullUrl, retryInit, timeoutMs);
        }
        // If still 401 after retry (or renewal failed), throw descriptive error
        if (response.status === 401) {
            throw new Error("Session expired. Please sign in again.");
        }
    }
    return response;
}
/**
 * Convenience wrapper for JSON POST requests.
 */
export async function apiPost(path, body) {
    const response = await apiFetch(path, {
        method: "POST",
        body: JSON.stringify(body),
    });
    if (!response.ok)
        await throwApiError(response, "POST", path);
    return response.json();
}
/**
 * Convenience wrapper for JSON GET requests.
 */
export async function apiGet(path) {
    const response = await apiFetch(path, { method: "GET" });
    if (!response.ok)
        await throwApiError(response, "GET", path);
    return response.json();
}
/**
 * Convenience wrapper for JSON PUT requests.
 */
export async function apiPut(path, body) {
    const response = await apiFetch(path, {
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!response.ok)
        await throwApiError(response, "PUT", path);
    return response.json();
}
/**
 * Convenience wrapper for DELETE requests. Tolerates an empty (204) body.
 */
export async function apiDelete(path) {
    const response = await apiFetch(path, { method: "DELETE" });
    if (!response.ok)
        await throwApiError(response, "DELETE", path);
}
// ─── Internal ───────────────────────────────────────────────────────────────
/**
 * Executes a fetch with a timeout enforced via AbortController.
 * Throws a descriptive error on network failure or timeout.
 */
async function timedFetch(url, init, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, Object.assign(Object.assign({}, init), { signal: controller.signal }));
    }
    catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            throw new Error(`Request to ${url} timed out after ${timeoutMs}ms. ` +
                `Check that the API is running and responsive.`);
        }
        const reason = error instanceof Error ? error.message : "Unknown network error";
        throw new Error(`Cannot reach the API server at ${url}. ` +
            `Reason: ${reason}. ` +
            `Check that the API is running and CORS allows this origin.`);
    }
    finally {
        clearTimeout(timer);
    }
}
